import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import styled from "styled-components";
import * as Yup from "yup";
import { loginSchema } from "../utils/validationSchemas";
import { loginUser, validateOfferToken } from "../services/api";
import { useAuth } from "../context/AuthContext";
import LoginFooter from "../components/LoginFooter";
import sidebarLogo from "../assets/sidebar_logo.svg";
import { FONTS } from "../utils/styleConstants";
import OfferPopup from "./OfferPopup.js";

const FormContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  height: 100%;
  margin: 0;
  position: relative;
  overflow: hidden;
  /* Fondo gradiente principal */
  background: linear-gradient(#02023d 0 22%, #bed0e9 55%, #1a2264 100%);
  display: flex;
  flex-direction: column;

  /* Triángulo oscuro superpuesto */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: calc(100% + 4px);
    height: 40vh;
    background: #02023d;
    clip-path: polygon(0 0, 100% 0, 100% 80%, 50% 95%, 0 80%);
    border-radius: 0 0 50px 50px;
    z-index: 1;
  }

  /* Gradiente de la punta del triángulo */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: calc(100% + 4px);
    height: 40vh;
    background: radial-gradient(
      circle at 50% 95%,
      rgba(255,255,255,0.8) 0%,
      rgba(255,255,255,0.2) 4%,
      rgba(255,255,255,0) 8%
    );
    clip-path: polygon(0 0, 100% 0, 100% 80%, 50% 95%, 0 80%);
    border-radius: 0 0 50px 50px;
    z-index: 1;
  }
`;

const TopLogo = styled.img`
  width: 40px;
  height: auto;
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 2;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 120px 24px 0;
  position: relative;
  z-index: 2;
  flex: 1;
  min-height: 0;
  
  @media (max-height: 700px) {
    padding-top: 80px;
  }
  
  @media (max-height: 600px) {
    padding-top: 60px;
  }
`;

const Title = styled.h1`
  color: #ff9773;
  font-size: min(38px, 8vh);
  text-align: left;
  margin: 0 0 8px;
  padding-left: 0;
  
  @media (max-height: 600px) {
    font-size: min(32px, 7vh);
    margin-bottom: 4px;
  }
`;

const Subtitle = styled.p`
  color: #ff5733;
  font-size: min(18px, 4vh);
  text-align: left;
  margin: 0 0 min(48px, 10vh);
  white-space: pre-line;
  padding-left: 0;
  
  @media (max-height: 600px) {
    font-size: min(16px, 3.5vh);
    margin-bottom: min(24px, 5vh);
  }
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 18vh;
  
  @media (max-height: 700px) {
    margin-top: 15vh;
    gap: 16px;
  }
`;

const Label = styled.label`
  font-size: min(1.7rem, 2.5vw);
  color: white;
  font-family: ${FONTS.SECONDARY};
  font-weight: 700;
  min-width: min(180px, 25%);
  white-space: nowrap;

  /* Ajuste específico para el label del DNI */
  &[for="id_number"] {
    min-width: min(160px, 22%);
    font-size: min(1.5rem, 2.2vw);
  }

  @media (max-width: 400px) {
    min-width: 100%;
    font-size: 14px !important;
    margin-bottom: 8px;
  }
`;

const Input = styled.input`
  padding: 0.25rem 0;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
  font-size: min(1.4rem, 2vw);
  font-family: ${FONTS.PRIMARY};
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;
  color: white;

  &:focus {
    border-bottom-color: white;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px transparent inset !important;
    -webkit-text-fill-color: white !important;
    transition: background-color 5000s ease-in-out 0s;
    font-size: inherit !important;
  }

  @media (max-width: 400px) {
    text-align: left;
    font-size: 16px;
    padding-right: ${props => props.type === 'password' ? '40px' : '0'};
  }
`;

const PasswordField = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const PasswordInput = styled(Input)`
  width: 100%;
  padding-right: 40px;
`;

const EyeIcon = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 400px) {
    right: 0;
    padding: 4px;
  }
`;

const HorizontalField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: min(1.5rem, 3vh);
  position: relative;
  padding: 0;

  .input-row {
    display: flex;
    align-items: center;
    gap: min(0.5rem, 1vw);
    min-height: 40px;
  }

  ${Label} {
    margin-bottom: 0;
    flex: 0 0 auto;
  }

  ${Input}, ${PasswordField} {
    flex: 1;
  }

  padding-bottom: 0.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);

  &:focus-within {
    border-bottom-color: white;
  }

  @media (max-width: 400px) {
    .input-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
    }

    border-bottom: none;

    ${Input}, ${PasswordField} {
      width: 100%;
      border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    }

    &:focus-within {
      ${Input}, ${PasswordField} {
        border-bottom-color: white;
      }
    }
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  position: absolute;
  bottom: -20px;
  right: 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
  padding: 0 24px;
  
  @media (max-height: 600px) {
    margin-top: 24px;
    gap: 12px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  border: 2px solid white;
  border-radius: 24px;
  background: transparent;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegisterLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-size: 14px;
  opacity: 0.8;
  transition: opacity 0.3s;
  text-align: center;

  &:hover {
    opacity: 1;
  }
`;

const ErrorIconSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 7V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="12" cy="16" r="1" fill="currentColor"/>
  </svg>
);

const EyeIconSvg = ({ isVisible }) =>
  isVisible ? (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
        fill="currentColor"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
        fill="currentColor"
      />
    </svg>
  );

const MobileLoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showOfferPopup, setShowOfferPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [domValues, setDomValues] = useState({ id_number: '', password: '' }); // Para forzar re-render
  const [autofillStatus, setAutofillStatus] = useState({ id_number: false, password: false }); // Para trackear autocomplete

  // Determinar el modo basado en la ruta
  const mode = location.pathname === '/forgot-password' ? 'forgot' :
               location.pathname === '/reset-password' ? 'reset' : 'login';
  
  const token = searchParams.get('token');

  // Validar token de oferta si existe en la URL
  useEffect(() => {
    const validateOfferTokenFromURL = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const offerToken = urlParams.get('token');
      
      if (offerToken) {
        try {
          const response = await validateOfferToken(offerToken);
          
          if (response.requiresLogin && response.showOfferPopup) {
            setPopupData({
              popupMessage: response.popupMessage,
              offerInfo: response.offer
            });
            setShowOfferPopup(true);
          }
        } catch (error) {
          console.error('Error validating offer token:', error);
        }
      }
    };

    validateOfferTokenFromURL();
  }, []);

  useEffect(() => {
    if (mode === 'reset' && !token) {
      setError('Enlace de recuperación inválido');
    }
  }, [mode, token]);

  // Esquemas de validación según el modo
  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email('Formato de email inválido')
      .required('El email es requerido')
  });

  const resetPasswordSchema = Yup.object().shape({
    newPassword: Yup.string()
      .matches(/^[a-zA-Z0-9]{8,16}$/, 'La contraseña debe tener entre 8 y 16 caracteres alfanuméricos')
      .required('La nueva contraseña es requerida'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], 'Las contraseñas no coinciden')
      .required('La confirmación de contraseña es requerida')
  });

  const getInitialValues = () => {
    switch (mode) {
      case 'forgot':
        return { email: "" };
      case 'reset':
        return { newPassword: "", confirmPassword: "" };
      default:
        return { id_number: "", password: "" };
    }
  };

  const getValidationSchema = () => {
    switch (mode) {
      case 'forgot':
        return forgotPasswordSchema;
      case 'reset':
        return resetPasswordSchema;
      default:
        return loginSchema;
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      setSuccess(false);

      try {
        if (mode === 'forgot') {
          // Solicitar recuperación de contraseña
          const { default: api } = await import('../services/api');
          const response = await api.post('/password-reset', values);
          setSuccess(true);
        } else if (mode === 'reset') {
          // Cambiar contraseña con token
          if (!token) {
            setError('Enlace de recuperación inválido');
            return;
          }

          const { default: api } = await import('../services/api');
          const response = await api.post('/password-reset/verify', {
            token: token,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword
          });
          
          setSuccess(true);
          // Redirigir al login después de 3 segundos
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          // Login normal
          await loginUser(values);
          
          // Verificar si hay una redirección pendiente guardada en sessionStorage ANTES de actualizar la autenticación
          const pendingRedirectUrl = sessionStorage.getItem("pendingRedirectUrl");
          const pendingOfferToken = sessionStorage.getItem("pendingOfferToken");
          
          // Actualizar estado de autenticación
          await login();
          
          if (pendingRedirectUrl && pendingOfferToken) {
            // Limpiar los datos de sessionStorage
            sessionStorage.removeItem("pendingRedirectUrl");
            sessionStorage.removeItem("pendingOfferToken");
            
            // Redirigir a la URL pendiente
            navigate(pendingRedirectUrl);
          } else {
            // Redirección por defecto
            navigate("/offers");
          }
        }
      } catch (error) {
        if (mode === 'login') {
          setError("No se pudo iniciar sesión. Verifica tus credenciales e inténtalo de nuevo.");
        } else if (mode === 'forgot') {
          setError(error.response?.data?.message || 'Error al enviar el correo de recuperación');
        } else if (mode === 'reset') {
          setError(error.response?.data?.message || 'Error al cambiar la contraseña');
        } else {
          setError('Error de conexión. Inténtalo de nuevo.');
        }
      } finally {
        setLoading(false);
      }
    },
  });

  // Detectar autocomplete y sincronizar con Formik
  useEffect(() => {
    const triggerAutocompleteConfirmation = () => {
      const idInput = document.getElementById('id_number');
      const passwordInput = document.getElementById('password');
      
      if (idInput && passwordInput) {
        const beforeClick = {
          idHasAutofill: idInput.matches && idInput.matches(':-webkit-autofill'),
          passwordHasAutofill: passwordInput.matches && passwordInput.matches(':-webkit-autofill')
        };
        
        // Actualizar estado de autocomplete para forzar re-render
        setAutofillStatus({
          id_number: beforeClick.idHasAutofill,
          password: beforeClick.passwordHasAutofill
        });
        
        // Simular clic global para confirmar el autocomplete de Google
        document.body.click();
        
        // Después del clic, verificar si aparecieron valores
        setTimeout(() => {
          const afterClick = {
            idHasAutofill: idInput.matches && idInput.matches(':-webkit-autofill'),
            passwordHasAutofill: passwordInput.matches && passwordInput.matches(':-webkit-autofill')
          };
          
          // Actualizar estado para forzar re-render
          const currentDomValues = {
            id_number: idInput.value || '',
            password: passwordInput.value || ''
          };
          setDomValues(currentDomValues);
          
          // Actualizar estado de autocomplete también
          setAutofillStatus({
            id_number: afterClick.idHasAutofill,
            password: afterClick.passwordHasAutofill
          });
          
          const idValue = idInput.value;
          const passwordValue = passwordInput.value;
          
          // Si hay valores después del clic, actualizarlos en Formik
          if (idValue && idValue !== formik.values.id_number) {
            formik.setFieldValue('id_number', idValue);
            formik.setFieldTouched('id_number', true, false);
          }
          if (passwordValue && passwordValue !== formik.values.password) {
            formik.setFieldValue('password', passwordValue);
            formik.setFieldTouched('password', true, false);
          }
        }, 50);
      }
    };

    // Ejecutar después del montaje y con algunos retrasos para cubrir diferentes navegadores
    const timeouts = [
      setTimeout(triggerAutocompleteConfirmation, 100),
      setTimeout(triggerAutocompleteConfirmation, 500),
      setTimeout(triggerAutocompleteConfirmation, 1000),
      setTimeout(triggerAutocompleteConfirmation, 2000)
    ];

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, []);

  // NO agregar estilos CSS que interfieren con el autocomplete

  const isFormValid = () => {
    if (mode === 'forgot') {
      return formik.values.email !== "" && !formik.errors.email;
    } else if (mode === 'reset') {
      return (
        formik.values.newPassword !== "" && 
        formik.values.confirmPassword !== "" && 
        !formik.errors.newPassword && 
        !formik.errors.confirmPassword
      );
    } else {
      // Login normal
      // Obtener valores tanto de Formik como del DOM
      const formikIdValue = formik.values.id_number;
      const formikPasswordValue = formik.values.password;
      
      const idInput = document.getElementById('id_number');
      const passwordInput = document.getElementById('password');
      const domIdValue = idInput?.value || '';
      const domPasswordValue = passwordInput?.value || '';
      
      // Detectar si tienen autocomplete de Google
      const idHasAutofill = idInput?.matches && idInput.matches(':-webkit-autofill');
      const passwordHasAutofill = passwordInput?.matches && passwordInput.matches(':-webkit-autofill');
      
      // Usar el valor que no esté vacío (priorizar Formik, fallback a DOM, fallback a autocomplete)
      const finalIdValue = formikIdValue || domIdValue || (idHasAutofill ? 'AUTOCOMPLETE_VALUE' : '');
      const finalPasswordValue = formikPasswordValue || domPasswordValue || (passwordHasAutofill ? 'AUTOCOMPLETE_VALUE' : '');
      
      // Verificar validación de Formik solo para los campos que están en Formik
      const hasFormikErrors = formik.errors.id_number || formik.errors.password;
      
      return (
        !hasFormikErrors &&
        finalIdValue !== "" &&
        finalPasswordValue !== ""
      );
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleClosePopup = () => {
    setShowOfferPopup(false);
    setPopupData(null);
  };

  return (
    <FormContainer>
      <TopLogo src={sidebarLogo} alt="Logo" />
      <ContentWrapper>
        <Title>
          {mode === 'forgot' ? 'Recuperar acceso' :
           mode === 'reset' ? 'Nueva contraseña' : 'Iniciar sesión'}
        </Title>
        <Subtitle>
          {mode === 'forgot' ? 
            `Introduce tu correo electrónico y te enviaremos\nun enlace para restablecer tu contraseña` :
           mode === 'reset' ?
            `Introduce tu nueva contraseña.\nDebe ser diferente a la anterior` :
            `Accede a tu cuenta para enviar, negociar\no validar ofertas vinculantes`}
        </Subtitle>
        {success ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ color: '#000049', fontSize: '1rem', marginBottom: '1rem', lineHeight: '1.5' }}>
              {mode === 'forgot' ?
                'Se ha enviado un correo con las instrucciones para recuperar tu contraseña. Revisa tu bandeja de entrada y la carpeta de spam.' :
                '¡Contraseña actualizada correctamente! Serás redirigido al inicio de sesión en unos segundos.'}
            </div>
            <RegisterLink to="/login">
              {mode === 'reset' ? 'Ir al inicio de sesión ahora' : 'Volver al inicio de sesión'}
            </RegisterLink>
          </div>
        ) : (
          <Form onSubmit={formik.handleSubmit}>
            {mode === 'login' && (
              <>
                <HorizontalField>
                  <div className="input-row">
                    <Label htmlFor="id_number">DNI, NIE</Label>
                    <Input
                      id="id_number"
                      name="id_number"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.id_number}
                    />
                  </div>
                  {formik.touched.id_number && formik.errors.id_number && (
                    <ErrorMessage>
                      <ErrorIconSvg />
                      {formik.errors.id_number}
                    </ErrorMessage>
                  )}
                </HorizontalField>

                <HorizontalField>
                  <div className="input-row">
                    <Label htmlFor="password">Contraseña</Label>
                    <PasswordField>
                      <PasswordInput
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password}
                      />
                      <EyeIcon type="button" onClick={togglePasswordVisibility}>
                        <EyeIconSvg isVisible={!showPassword} />
                      </EyeIcon>
                    </PasswordField>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <ErrorMessage>
                      <ErrorIconSvg />
                      {formik.errors.password}
                    </ErrorMessage>
                  )}
                </HorizontalField>
              </>
            )}

            {mode === 'forgot' && (
              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="email">Correo electrónico</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="tu@email.com"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <ErrorMessage>
                    <ErrorIconSvg />
                    {formik.errors.email}
                  </ErrorMessage>
                )}
              </HorizontalField>
            )}

            {mode === 'reset' && (
              <>
                <HorizontalField>
                  <div className="input-row">
                    <Label htmlFor="newPassword">Nueva contraseña</Label>
                    <PasswordField>
                      <PasswordInput
                        id="newPassword"
                        name="newPassword"
                        type={showPassword ? "text" : "password"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.newPassword}
                      />
                      <EyeIcon type="button" onClick={togglePasswordVisibility}>
                        <EyeIconSvg isVisible={!showPassword} />
                      </EyeIcon>
                    </PasswordField>
                  </div>
                  {formik.touched.newPassword && formik.errors.newPassword && (
                    <ErrorMessage>
                      <ErrorIconSvg />
                      {formik.errors.newPassword}
                    </ErrorMessage>
                  )}
                </HorizontalField>

                <HorizontalField>
                  <div className="input-row">
                    <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                    <PasswordField>
                      <PasswordInput
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.confirmPassword}
                      />
                      <EyeIcon type="button" onClick={toggleConfirmPasswordVisibility}>
                        <EyeIconSvg isVisible={!showConfirmPassword} />
                      </EyeIcon>
                    </PasswordField>
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <ErrorMessage>
                      <ErrorIconSvg />
                      {formik.errors.confirmPassword}
                    </ErrorMessage>
                  )}
                </HorizontalField>
              </>
            )}

            <ButtonContainer>
              {error && (
                <ErrorMessage>
                  <ErrorIconSvg />
                  {error}
                </ErrorMessage>
              )}
              <SubmitButton type="submit" disabled={loading || !isFormValid()}>
                {loading ? 
                  (mode === 'forgot' ? 'Enviando...' : 
                   mode === 'reset' ? 'Actualizando...' : 'Iniciando sesión...') :
                  (mode === 'forgot' ? 'Recuperar' :
                   mode === 'reset' ? 'Actualizar' : 'Iniciar sesión')}
              </SubmitButton>
              
              {mode === 'login' && (
                <>
                  <RegisterLink to="/register">
                    ¿No tienes cuenta? Regístrate aquí
                  </RegisterLink>
                  
                  <RegisterLink to="/forgot-password">
                    ¿Olvidaste tu contraseña?
                  </RegisterLink>
                </>
              )}

              {(mode === 'forgot' || mode === 'reset') && (
                <RegisterLink to="/login">
                  Volver al inicio de sesión
                </RegisterLink>
              )}
            </ButtonContainer>
          </Form>
        )}
      </ContentWrapper>
      <LoginFooter />
      
      {/* Popup de oferta vinculante */}
      <OfferPopup
        isOpen={showOfferPopup}
        onClose={handleClosePopup}
        popupMessage={popupData?.popupMessage}
        offerInfo={popupData?.offerInfo}
      />
    </FormContainer>
  );
};

export default MobileLoginForm; 